import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Popover,
  Typography,
} from '@mui/material';
import { ViewColumn as ViewColumnIcon } from '@mui/icons-material';

export interface ReportColumnOption {
  key: string;
  label: string;
  required?: boolean;
}

interface ReportColumnPickerProps {
  columns: ReportColumnOption[];
  visibleColumns: string[];
  onChange: (columns: string[]) => void;
  onReset?: () => void;
  buttonLabel?: string;
}

const ReportColumnPicker: React.FC<ReportColumnPickerProps> = ({
  columns,
  visibleColumns,
  onChange,
  onReset,
  buttonLabel = 'Columns',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<string[]>(visibleColumns);

  useEffect(() => {
    if (!anchorEl) {
      setDraft(visibleColumns);
    }
  }, [visibleColumns, anchorEl]);

  const open = Boolean(anchorEl);

  const toggleColumn = (key: string, required?: boolean) => {
    if (required) return;
    setDraft((current) => (
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key]
    ));
  };

  const handleApply = () => {
    const requiredKeys = columns.filter((column) => column.required).map((column) => column.key);
    const next = Array.from(new Set([...requiredKeys, ...draft]));
    onChange(next);
    setAnchorEl(null);
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    } else {
      setDraft(columns.map((column) => column.key));
    }
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ViewColumnIcon />}
        onClick={(event) => {
          setDraft(visibleColumns);
          setAnchorEl(event.currentTarget);
        }}
      >
        {buttonLabel}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Visible columns
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Saved for your account. Applies to the grid, exports, and sharing.
          </Typography>
          <FormGroup>
            {columns.map((column) => (
              <FormControlLabel
                key={column.key}
                control={(
                  <Checkbox
                    size="small"
                    checked={draft.includes(column.key)}
                    disabled={column.required}
                    onChange={() => toggleColumn(column.key, column.required)}
                  />
                )}
                label={column.label}
              />
            ))}
          </FormGroup>
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Button size="small" onClick={handleReset} disabled={!onReset}>
              Reset
            </Button>
            <Button size="small" variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ReportColumnPicker;
